import React from "react";
import {
    Factory,
    FlaskConical,
    Beer,
    Store,
    Truck,
    PackageCheck,
} from "lucide-react";
import wineBottle from "../../assets/production-img.png";
import productionBgIcon from "../../assets/production-bg-icon.png";
import { motion } from "framer-motion";

const leftItems = [
    {
        icon: <Factory size={40} strokeWidth={1.8} />,
        title: "Brewery Process",
        desc: "Our journey starts with the careful selection of high-quality ingredients, followed by a meticulous brewing process handled by expert vintners and brewers.",
    },
    {
        icon: <FlaskConical size={40} strokeWidth={1.8} />,
        title: "Barrel Fermentation",
        desc: "Fermented in handcrafted oak barrels, the wine matures over time, absorbing the rich flavors and aromas unique to each vintage.",
    },
    {
        icon: <Beer size={40} strokeWidth={1.8} />,
        title: "Draft Beer",
        desc: "Poured fresh and cold, our draft beer retains the bold character and essence of traditional brewing, served straight from the tap.",
    },
];

const rightItems = [
    {
        icon: <Store size={40} strokeWidth={1.8} />,
        title: "Pub Services",
        desc: "Enjoy curated tasting experiences in our elegantly designed pubs where hospitality meets craftsmanship in every pour.",
    },
    {
        icon: <Truck size={40} strokeWidth={1.8} />,
        title: "Barrel Transport",
        desc: "We ensure secure and stable transport of every barrel using custom-designed logistics to preserve its integrity and taste.",
    },
    {
        icon: <PackageCheck size={40} strokeWidth={1.8} />,
        title: "Beer Packaging",
        desc: "Our final step involves premium-grade packaging that locks in freshness, flavor, and finesse — ready to be enjoyed worldwide.",
    },
];

const WineProduction = () => {
    return (
        <section className="w-full min-h-[100vh] bg-[--neutral-white] py-20 px-6 md:px-12 relative overflow-hidden">
            {/* Section Heading */}
            <motion.h2
                className="text-4xl sm:text-5xl font-extrabold text-[--primary-wine] leading-tight text-center mb-16 relative z-10"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
            >
                <span className="text-[--neutral-black]">Wine </span>
                <span className="relative inline-block text-[--neutral-black]">
                    Journey
                    <span className="absolute bottom-1 left-0 w-full h-[6px] bg-[--accent-gold] -z-10 rounded-md"></span>
                </span>
            </motion.h2>


            <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-12">

                {/* Left Column */}
                <div className="flex flex-col gap-14 w-full md:w-1/3 relative z-20">
                    {leftItems.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.2, duration: 0.6 }}
                            viewport={{ once: true }}
                            className="flex items-start justify-end gap-6 text-right"
                        >
                            <div className="flex flex-col items-end max-w-xs">
                                <h4 className="text-xl font-semibold text-[--primary-wine]">{item.title}</h4>
                                <p className="text-sm text-[--neutral-gray] mt-1">{item.desc}</p>
                            </div>
                            <motion.div
                                className="min-w-[60px] min-h-[60px] flex items-center justify-center rounded-full bg-[--primary-wine-light] text-[--neutral-white] shadow-sm"
                                initial={{ rotate: -20, opacity: 0 }}
                                whileInView={{ rotate: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: index * 0.2 }}
                                viewport={{ once: true }}
                            >
                                {item.icon}
                            </motion.div>
                        </motion.div>
                    ))}
                </div>

                {/* Center Bottle */}
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.9, type: "spring", bounce: 0.3 }}
                    viewport={{ once: true }}
                    className="w-full md:w-1/3 flex justify-center z-20"
                >
                    <img
                        src={wineBottle}
                        alt="Wine Bottle"
                        className="w-72 md:w-full drop-shadow-xl transition-transform scale-110"
                    />
                </motion.div>

                {/* Right Column */}
                <div className="flex flex-col gap-14 w-full md:w-1/3">
                    {rightItems.map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.2, duration: 0.6 }}
                            viewport={{ once: true }}
                            className="flex items-start gap-6 text-left"
                        >
                            <motion.div
                                className="min-w-[60px] min-h-[60px] flex items-center justify-center rounded-full bg-[--primary-wine-light] text-[--neutral-white] shadow-sm"
                                initial={{ rotate: 20, opacity: 0 }}
                                whileInView={{ rotate: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: index * 0.2 }}
                                viewport={{ once: true }}
                            >
                                {item.icon}
                            </motion.div>
                            <div className="flex flex-col items-start max-w-xs">
                                <h4 className="text-xl font-semibold text-[--primary-wine]">{item.title}</h4>
                                <p className="text-sm text-[--neutral-gray] mt-1">{item.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>

            {/* Background Icon (Static) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.04] z-10">
                <img src={productionBgIcon} alt="" className="w-full max-w-[500px]" />
            </div>
        </section>
    );
};

export default WineProduction;
